import React, { useCallback } from 'react';
import type { ViewStyle } from 'react-native';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import type { usePlacesAutocomplete } from '@sweetgreen/garnish';
import {
  getSubstringsByPositions,
  HighlightWords,
  theme,
  useBreakpointHelpers,
  usePressableState,
  VStack,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import type { Customer } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { ErrorMessage } from './ErrorMessage';
import { LoadingResults } from './LoadingResults/LoadingResults.desktop';
import { NoResults } from './NoResults';

export const AddressSearchResults = React.forwardRef(
  (props: AddressSearchResultsProps, ref: React.Ref<ScrollView>) => {
    const { currentBreakpoint } = useBreakpointHelpers();
    const {
      customerAddresses,
      predictionsData,
      skipLoading,
      useDesktopStyles,
      style,
      onPress,
    } = props;
    const { loading, error, noResults, predictions } = predictionsData;

    const containerStyles = [
      currentBreakpoint.isMobile && !useDesktopStyles
        ? styles.mobileAddressSearchResultsContainer
        : styles.desktopAddressSearchResultsContainer,
      style,
    ];

    return (
      <ScrollView
        testID="location.auto-complete-results"
        ref={ref}
        style={containerStyles}
      >
        {loading && !skipLoading ? (
          <LoadingResults />
        ) : noResults ? (
          <NoResults noAutocompleteResults />
        ) : (
          <VStack customSpacing={0} divider>
            {predictions
              .filter(({ place_id }) => Boolean(place_id))
              .map((location) => (
                <AddressSearchResult
                  key={location.place_id}
                  placeId={location.place_id}
                  customerAddresses={customerAddresses}
                  text={removeCountry(location.description, location.terms)}
                  matchedSubstrings={location.matched_substrings}
                  onPress={onPress}
                />
              ))}
          </VStack>
        )}

        {error ? <ErrorMessage minimalStyles /> : null}
      </ScrollView>
    );
  },
);

const removeCountry = (location: string, terms: readonly Term[]): string => {
  const lastTerm = (terms?.length ?? 1) - 1;
  const countryTerm = terms?.[lastTerm]?.value;

  if (!countryTerm) return location;
  const country = `, ${countryTerm}`;

  if (location.endsWith(country)) return location.replace(country, '');

  return location;
};

// ----------------------------------------
// ---- subcomponents
// ----------------------------------------

export const AddressSearchResult = ({
  placeId,
  text,
  matchedSubstrings,
  customerAddresses,
  onPress,
}: {
  readonly placeId: string;
  readonly text: string;
  readonly matchedSubstrings: readonly google.maps.places.PredictionSubstring[];
  readonly customerAddresses?: Customer['addresses'];
  readonly onPress?: (placeId: string) => void;
}) => {
  const { t } = useLocalizationContext();

  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);
  const highlights = getSubstringsByPositions(text, matchedSubstrings);
  const existingAddress = customerAddresses?.find(
    ({ googlePlaceId }) => placeId === googlePlaceId,
  );
  const customerAddress =
    existingAddress?.name && `${existingAddress.name} - ${text}`;
  const address = customerAddress ?? text;

  const handleOnPress = useCallback(() => {
    if (onPress) onPress(placeId);
  }, [placeId, onPress]);

  return (
    <TouchableOpacity
      ref={ref}
      onPress={handleOnPress}
      accessibilityRole="button"
      accessibilityLabel={t('account.addresses.select-location.label', {
        text,
      })}
      accessibilityHint={t('account.addresses.select-location.hint')}
      testID={`location-search-result-${text}`}
    >
      <View
        style={[
          styles.addressSearchResult,
          webOnlyStyles({ transition: `color ${theme.transitions.base}ms` }),
        ]}
        pointerEvents="box-only"
      >
        <HighlightWords
          text={address}
          highlights={highlights}
          numberOfLines={1}
          underline={isInteracting}
        />
      </View>
    </TouchableOpacity>
  );
};

// ──── Types ──────────────────────────────────────────────────

type AddressSearchResultsProps = {
  readonly style?: ViewStyle;
  readonly skipLoading?: boolean;
  readonly customerAddresses?: Customer['addresses'];
  readonly useDesktopStyles?: boolean;
  readonly predictionsData: Pick<
    ReturnType<typeof usePlacesAutocomplete>,
    'noResults' | 'error' | 'loading' | 'predictions'
  >;
  readonly onPress?: (placeId: string) => void;
};

type Term = { readonly value: string };

// ──── Styles ─────────────────────────────────────────────────

const styles = StyleSheet.create({
  desktopAddressSearchResultsContainer: {
    backgroundColor: theme.colors.OATMEAL,
    padding: theme.spacing.medium,
  },
  mobileAddressSearchResultsContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed + 1, // cover up components that are below
    padding: theme.spacing.medium,
    backgroundColor: theme.colors.OATMEAL,
  },
  addressSearchResult: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
  },
});
